<script lang="ts">
  import { DisplayText, Link, type LinkProps } from '@squareup/public-web-design-system';
  import type { PwNextCallToActionProps } from './types';
  import DynamicChildrenComponent from '$lib/components/DynamicChildrenComponent/DynamicChildrenComponent.svelte';

  export let entryId: PwNextCallToActionProps['entryId'];
  export let customCssClass: PwNextCallToActionProps['customCssClass'];
  export let direction: PwNextCallToActionProps['direction'];
  export let alignment: PwNextCallToActionProps['alignment'];
  export let verticalAlignment: PwNextCallToActionProps['verticalAlignment'];
  export let heading: PwNextCallToActionProps['heading'];
  // Eyebrow, Subheading, and Body were added to have additional copy on Hardware pages for pricing sections
  // https://github.com/squareup/xms-fe/pull/1228
  export let eyebrow: PwNextCallToActionProps['eyebrow'];
  export let subheading: PwNextCallToActionProps['subheading'];
  export let body: PwNextCallToActionProps['body'];
  export let links: PwNextCallToActionProps['links'];
  export let form: PwNextCallToActionProps['form'];

  const alignItemsMap: Record<PwNextCallToActionProps['alignment'], string> = {
    left: 'flex-start',
    right: 'flex-end',
    center: 'center',
  };

  const verticalAlignMap: Record<PwNextCallToActionProps['verticalAlignment'], string> = {
    top: 'flex-start',
    middle: 'center',
    bottom: 'flex-end',
    baseline: 'baseline',
  };

  function getLinkVariant(idx: number): LinkProps['variant'] {
    return idx % 2 === 0 ? 'button-alt' : 'button';
  }
</script>

<section
  class="PwNextCallToAction PwNextCallToAction-{entryId}-box-properties {customCssClass}"
  data-entry-id={entryId}
  style:--pw-next-call-to-action-align-items={form ? 'flex-start' : alignItemsMap[alignment]}
  style:--pw-next-call-to-action-text-align={form ? 'left' : alignment}
  style:--pw-next-call-to-action-vertical-align={form ? 'flex-start' : verticalAlignMap[verticalAlignment]}
>
  <div class="PwNextCallToAction__container PwNextCallToAction__container--{direction}">
    <div>
      {#if eyebrow}
        <DynamicChildrenComponent children={[eyebrow]} />
      {/if}
      {#if heading}
        <DisplayText variant={direction === 'column' ? 'large' : 'small'}>
          {heading}
        </DisplayText>
      {/if}
      {#if subheading}
        <DynamicChildrenComponent children={[subheading]} />
      {/if}
    </div>
    <div class:PwNextCallToAction__body-group={body || form}>
      {#if body}
        <DynamicChildrenComponent children={[body]} passThroughProps={{ customCssClass: 'PwNextCallToAction__body' }} />
      {/if}
      {#if links && links.length > 0}
        <div class="PwNextCallToAction__links">
          {#each links as link, idx}
            {@const { url, target, nofollow, size, text } = link.props}
            <Link variant={getLinkVariant(idx)} href={url} {target} rel={nofollow} {size} {text} />
          {/each}
        </div>
      {/if}
      {#if form}
        <DynamicChildrenComponent
          children={form}
          passThroughProps={{
            customCssClass: 'PwNextCallToAction__form',
            introTextAlignment: 'left',
          }}
        />
      {/if}
    </div>
  </div>
</section>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/tokens/generated-tokens/scss/_media_queries' as media;

  @layer custom-components {
    .PwNextCallToAction {
      &__container {
        display: flex;
        flex-direction: column;
        // Defaults to align-items: center;
        align-items: var(--pw-next-call-to-action-align-items);
        // Set the Box Width to content width, so the parent element can use Background Color in Box Properties without
        // wrapping the Custom Component in another element
        width: var(--container-width-current);
        margin: {
          right: var(--space-auto);
          left: var(--space-auto);
        }

        // Defaults to text-align: center;
        text-align: var(--pw-next-call-to-action-text-align);

        & > div:last-child {
          flex-shrink: 0;
        }

        // Smaller screens will always have flex-direction: column; styling, so there needs to be padding-top only until
        // flex-direction: row; is possible
        .PwNextCallToAction__links {
          @include media.until('medium') {
            padding-top: var(--space-40);
          }
        }

        &--column {
          .PwNextCallToAction__links {
            @include media.at('medium') {
              padding-top: var(--space-40);
            }
          }
        }

        .PwNextCallToAction__body-group {
          margin-top: var(--space-40);

          @include media.at('medium') {
            margin-top: var(--space-none);
          }

          :global {
            .PwNextCallToAction__body {
              // Remove margin-top in case it was added through Contentful because the styling should be set here to make
              // it consistent across all uses
              margin-top: var(--space-none) !important;

              & + .PwNextCallToAction__links {
                @include media.at('medium') {
                  padding-top: var(--space-30);
                }
              }
            }
          }
        }

        &--row {
          @include media.until('medium') {
            :global {
              // Overriding variant={700} styling to use tokens rather than adding a resize event listener to make the
              // change
              .DisplayText {
                --font-size: var(--text-variant-800-font-size);
                --line-height: var(--text-variant-800-line-height);
                --letter-spacing: var(--text-variant-800-letter-spacing);
              }
            }
          }

          @include media.at('medium') {
            --pw-next-call-to-action-text-align: left;

            align-items: var(--pw-next-call-to-action-vertical-align);
            flex-direction: row;
            justify-content: space-between;
            gap: var(--space-60);
          }
        }
      }

      &__body-group {
        @include media.at('medium') {
          max-width: 50%;
        }

        :global {
          p {
            // Since .PwNextCallToAction__body-group's parent has flex-shrink: 0 it was causing items that are used as
            // spans to prevent margin-top styling to wrap on one line. Making them block elements prevents that
            .InlineTypographyComponent + .InlineTypographyComponent {
              display: block;
            }
          }

          * + .PwNextCallToAction__form {
            margin-top: var(--space-60);
          }
        }
      }

      &__links {
        display: flex;
        flex-wrap: wrap;
        justify-content: var(--pw-next-call-to-action-align-items);
        gap: var(--space-20);

        @include media.at('medium') {
          flex-wrap: nowrap;
          justify-content: normal;
        }
      }

      :global {
        .PwNextCallToAction__form {
          // Ignore Contentful settings because we always want the form in PwNextCallToAction to be left aligned
          .TextStack {
            --text-stack-cta-gap: var(--space-30) !important;
            // Even though .PwNextCallToAction__form is using passThroughProps the introTextAlignment prop is not passed
            // to the FollowUpLeadForm, so we still need to add the styling overrides here
            --text-stack-flex-alignment: flex-start !important;
            --text-stack-text-alignment: left !important;
            --text-stack-optical-text-indent: 0.1ch !important;
          }

          // LeadForm section sets a width on content, so overriding it to make sure the form doesn't overflow
          .content,
          // FollowUpLeadFormComponent sets a width on content-width-large
          .content-width-large {
            width: auto !important;
          }

          // LeadForm sets a width on singleFormField. This can be seen in a form with 1 field like Passive General
          // Marketo form that only captures an email address
          .singleFieldForm,
          // LeadForm sets a width on row-with-1-columns. This can be seen in a form where you have to acknowledge you
          // agree to the terms like the Master Passive Follow Up Section
          .row-with-1-columns > * {
            width: 100% !important;
          }

          // The LeadFormSection only takes up half of the window, so having 2 columns on medium size screens is not
          // leaving enough room for the fields
          .row-with-2-columns {
            // Row styling uses a max-width media query, so also using max-width here in case they ever change from flex
            // to grid
            @include media.until('large') {
              display: block;
            }
          }
        }
      }
    }
  }
</style>
